/**
 * useWebRTCHealth - Traffic-light health of the live peer connections
 *
 * Polls while the panel is open only: the connections' state machines move
 * without a React-visible event, and there is nothing to watch when the panel
 * is closed.
 */

import type { DevToolsHealthStatus } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/index.types';
import { getWebRTCHealthStatus } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

export const WEBRTC_HEALTH_POLL_INTERVAL_MS = 1000;

export const useWebRTCHealth = (isOpen: boolean): DevToolsHealthStatus => {
  const { activeCalls, activeConference } = useSoftphoneStore(
    useShallow((state) => ({
      activeCalls: state.activeCalls,
      activeConference: state.activeConference,
    }))
  );
  const [webrtcHealth, setWebrtcHealth] =
    useState<DevToolsHealthStatus>('idle');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updateWebRTCHealth = () => {
      setWebrtcHealth(
        getWebRTCHealthStatus(
          Array.from(activeCalls.values()),
          WebRTCService.getInstance(),
          activeConference?.roomGuid ?? null
        )
      );
    };

    updateWebRTCHealth();
    const interval = setInterval(
      updateWebRTCHealth,
      WEBRTC_HEALTH_POLL_INTERVAL_MS
    );

    return () => clearInterval(interval);
  }, [isOpen, activeCalls, activeConference]);

  return webrtcHealth;
};
